import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { useToast } from '@/components/ui/use-toast';
import { config } from '@/lib/config';
import { User, Mic, Globe, Camera, Save, Check, AlertCircle, Radio } from 'lucide-react';
import { db } from '@/lib/firebase';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { generateUrlSlug as genUrlSlug } from '@/lib/utils';

const KaraokeProfilePage = () => {
  const { user, profile, refreshProfile } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isFirstTimeSetup, setIsFirstTimeSetup] = useState(false);
  const [formData, setFormData] = useState({
    karaoke_name: '',
    url_slug: '',
    bio: '',
    contact_email: '',
    contact_phone: '',
    profile_image_url: ''
  });

  // Load profile data
  useEffect(() => {
    if (profile) {
      setFormData({
        karaoke_name: profile.karaoke_name || '',
        url_slug: profile.url_slug || '',
        bio: profile.bio || '',
        contact_email: profile.contact_email || user?.email || '',
        contact_phone: profile.contact_phone || '',
        profile_image_url: profile.profile_image_url || ''
      });
      
      // Check if this is first-time setup (no karaoke_name or url_slug)
      const isFirstTime = !profile.karaoke_name || !profile.url_slug;
      setIsFirstTimeSetup(isFirstTime);
    }
  }, [profile, user]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const generateUrlSlug = () => {
    const name = formData.karaoke_name || user?.displayName || user?.email?.split('@')[0] || 'karaoke';
    const slug = genUrlSlug(name);
    setFormData(prev => ({
      ...prev,
      url_slug: slug
    }));
  };

  const handleSave = async () => {
    if (!user?.uid) {
      toast({
        title: "Authentication Error",
        description: "Please log in to save your profile",
        variant: "destructive"
      });
      return;
    }

    if (!formData.karaoke_name.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter your karaoke name",
        variant: "destructive"
      });
      return;
    }

    if (!profile?.url_slug && !formData.url_slug.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter a URL slug",
        variant: "destructive"
      });
      return;
    }

    setSaving(true);

    try {
      const profileRef = doc(db, 'profiles', user.uid);
      
      // Check if URL slug is already taken by another user (only if it's being changed, case-insensitive)
      if (formData.url_slug !== profile?.url_slug && formData.url_slug.trim()) {
        const urlCheckQuery = await getDoc(doc(db, 'url_slugs', formData.url_slug.toLowerCase()));
        if (urlCheckQuery.exists() && urlCheckQuery.data().user_id !== user.uid) {
          toast({
            title: "URL Already Taken",
            description: "This URL slug is already in use. Please choose a different one.",
            variant: "destructive"
          });
          setSaving(false);
          return;
        }
      }

      const updateData = {
        karaoke_name: formData.karaoke_name.trim(),
        bio: formData.bio.trim(),
        contact_email: formData.contact_email.trim(),
        contact_phone: formData.contact_phone.trim(),
        profile_image_url: formData.profile_image_url.trim(),
        updated_at: new Date()
      };

      // Only update URL slug if it's not already set or if it's being changed (store in lowercase)
      if (!profile?.url_slug || formData.url_slug !== profile?.url_slug) {
        updateData.url_slug = formData.url_slug.trim().toLowerCase();
      }

      await updateDoc(profileRef, updateData);

      // Update URL slug mapping if it changed or if it's being set for the first time (use lowercase)
      if (formData.url_slug !== profile?.url_slug && formData.url_slug.trim()) {
        const urlSlugRef = doc(db, 'url_slugs', formData.url_slug.toLowerCase());
        await setDoc(urlSlugRef, {
          user_id: user.uid,
          account_type: 'karaoke',
          created_at: new Date(),
          updated_at: new Date()
        });
      }

      await refreshProfile();

      toast({
        title: "Profile Updated",
        description: "Your karaoke profile has been saved successfully",
        variant: "default"
      });

    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Update Failed",
        description: "There was an error saving your profile. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const getPublicUrl = () => {
    if (!formData.url_slug) return null;
    return `${config.getBaseUrl()}/karaoke/${formData.url_slug}`;
  };

  return (
    <>
      <Helmet>
        <title>Karaoke Profile - Your Song Request</title>
        <meta name="description" content="Manage your karaoke profile and settings." />
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-purple-100">Karaoke Profile</h1>
          <p className="text-purple-300 mt-1">Manage your karaoke profile and public information</p>
        </div>

        {/* Profile Setup Warning */}
        {!profile?.url_slug && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 bg-purple-900/20 border border-purple-700/50 rounded-lg"
          >
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-purple-400 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-purple-200 font-semibold mb-2">Complete Your Profile</h3>
                <p className="text-purple-300 text-sm">
                  Set up your karaoke profile to start accepting song requests and managing sessions.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Main Profile Form */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Basic Information */}
            <Card className="bg-purple-900/20 border-purple-700">
              <CardHeader>
                <CardTitle className="text-purple-100 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Basic Information
                </CardTitle>
                <CardDescription className="text-purple-300">
                  Your karaoke profile details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={isFirstTimeSetup ? "ring-2 ring-green-500/50 rounded-lg p-3 bg-green-900/20" : ""}>
                    <Label htmlFor="karaoke_name" className={`text-purple-200 ${isFirstTimeSetup ? "text-green-300 font-semibold" : ""}`}>
                      DJ Name *
                      {isFirstTimeSetup && <span className="text-green-400 ml-1">(Required)</span>}
                    </Label>
                    <Input
                      id="karaoke_name"
                      value={formData.karaoke_name}
                      onChange={(e) => handleInputChange('karaoke_name', e.target.value)}
                      placeholder="Your DJ name"
                      className={`bg-purple-800/30 border-purple-600 text-purple-100 placeholder:text-purple-400 ${isFirstTimeSetup ? "border-green-500/50 focus:border-green-400" : ""}`}
                    />
                  </div>
                  <div className={isFirstTimeSetup ? "ring-2 ring-green-500/50 rounded-lg p-3 bg-green-900/20" : ""}>
                    <Label htmlFor="url_slug" className={`text-purple-200 ${isFirstTimeSetup ? "text-green-300 font-semibold" : ""}`}>
                      Profile URL
                      {isFirstTimeSetup && <span className="text-green-400 ml-1">(Required)</span>}
                      {profile?.url_slug && <span className="text-purple-400 ml-1">(Set during signup)</span>}
                    </Label>
                    <div className="flex space-x-2">
                      <Input
                        id="url_slug"
                        value={formData.url_slug}
                        onChange={(e) => handleInputChange('url_slug', e.target.value)}
                        placeholder="your-dj-name"
                        readOnly={!!profile?.url_slug}
                        className={`bg-purple-800/30 border-purple-600 text-purple-100 placeholder:text-purple-400 ${isFirstTimeSetup ? "border-green-500/50 focus:border-green-400" : ""} ${profile?.url_slug ? "opacity-60 cursor-not-allowed" : ""}`}
                      />
                      {!profile?.url_slug && (
                        <Button
                          onClick={generateUrlSlug}
                          variant="outline"
                          size="sm"
                          className="border-purple-600 text-purple-300 hover:bg-purple-600/20"
                        >
                          Generate
                        </Button>
                      )}
                    </div>
                    {formData.url_slug && (
                      <p className="text-purple-400 text-sm mt-1">
                        Your URL: {getPublicUrl()}
                      </p>
                    )}
                    {profile?.url_slug && (
                      <p className="text-green-400 text-sm mt-1">
                        ✓ URL slug set during signup - cannot be changed
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_email" className="text-purple-200">Contact Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => handleInputChange('contact_email', e.target.value)}
                      placeholder="contact@yourvenue.com"
                      className="bg-purple-800/30 border-purple-600 text-purple-100 placeholder:text-purple-400"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_phone" className="text-purple-200">Contact Phone</Label>
                    <Input
                      id="contact_phone"
                      value={formData.contact_phone}
                      onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                      placeholder="+44 123 456 7890"
                      className="bg-purple-800/30 border-purple-600 text-purple-100 placeholder:text-purple-400"
                    />
                  </div>
                </div>
                
                {/* Save Button for Basic Information */}
                <div className="pt-4 border-t border-purple-700/50">
                  <Button
                    onClick={handleSave}
                    disabled={saving || !formData.karaoke_name.trim() || (!profile?.url_slug && !formData.url_slug.trim())}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Basic Information
                      </>
                    )}
                  </Button>
                </div>
                
                <div>
                  <Label htmlFor="bio" className="text-purple-200">Bio</Label>
                  <Textarea
                    id="bio"
                    value={formData.bio}
                    onChange={(e) => handleInputChange('bio', e.target.value)}
                    placeholder="Tell people about your karaoke setup..."
                    rows={3}
                    className="bg-purple-800/30 border-purple-600 text-purple-100 placeholder:text-purple-400"
                  />
                </div>
              </CardContent>
            </Card>







            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Profile
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Preview Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Profile Preview */}
            <Card className="bg-purple-900/20 border-purple-700">
              <CardHeader>
                <CardTitle className="text-purple-100 flex items-center">
                  <Camera className="w-5 h-5 mr-2" />
                  Profile Preview
                </CardTitle>
                <CardDescription className="text-purple-300">
                  How your profile will appear
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center p-4 bg-purple-800/30 rounded-lg">
                  {formData.profile_image_url ? (
                    <img
                      src={formData.profile_image_url}
                      alt="Profile"
                      className="w-20 h-20 rounded-full mx-auto mb-3 object-cover"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-purple-600 rounded-full mx-auto mb-3 flex items-center justify-center">
                      <Mic className="w-8 h-8 text-white" />
                    </div>
                  )}
                  
                  <h3 className="text-purple-100 font-semibold">
                    {formData.karaoke_name || 'Your Karaoke Name'}
                  </h3>
                  
                  {formData.venue_name && (
                    <p className="text-purple-400 text-sm">
                      {formData.venue_name}
                    </p>
                  )}
                  
                  {formData.bio && (
                    <p className="text-purple-300 text-sm mt-2">
                      {formData.bio}
                    </p>
                  )}
                  
                  {getPublicUrl() && (
                    <div className="mt-3 p-2 bg-purple-800/50 rounded text-xs text-purple-300">
                      {getPublicUrl()}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-purple-900/20 border-purple-700">
              <CardHeader>
                <CardTitle className="text-purple-100">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {getPublicUrl() && (
                  <Button
                    onClick={() => window.open(getPublicUrl(), '_blank')}
                    variant="outline"
                    className="w-full border-purple-600 text-purple-300 hover:bg-purple-600/20"
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    View Public Page
                  </Button>
                )}
                
                                <Button
                  onClick={() => window.open(`${config.getBaseUrl()}/karaoke/admin`, '_self')}
                  variant="outline"
                  className="w-full border-purple-600 text-purple-300 hover:bg-purple-600/20"
                >
                  <Radio className="w-4 h-4 mr-2" />
                  Go to Dashboard
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default KaraokeProfilePage;
